/** @jsxImportSource @emotion/react */
import {
  AccountInfo,
  InteractionRequiredAuthError,
  InteractionType,
} from '@azure/msal-browser';
import { useMsal, useMsalAuthentication } from '@azure/msal-react';
import { Global } from '@emotion/react';
import { css } from '@emotion/react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import HomePage from 'components/overview';
import moment from 'moment';
import { createContext, useEffect, useState } from 'react';
import Helmet from 'react-helmet';
import { Route, BrowserRouter as Router, Routes } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import Header from './components/common/Header';
import { loginRequest } from './config/authConfig';
import { SessionProvider } from './providers/sessionProvider';
// import { AccountResponse } from './@types/msAccount';
import './styles/App.css';
import { COLORS } from './styles/colors';
// import { fetchUser } from './services/msgraph';
import globalStyles from './styles/globalStyles';
import { login } from './utils/apiHelper';

export const AppToken = createContext('');

function App() {
  // Remove these lines to disable sso authentication, meaning everyone (non-cx users) can access to the site
  /*--------------------------------------------------------------------------------------------------------*/
  const { error } = useMsalAuthentication(InteractionType.Redirect);
  if (error) {
    throw new Error(`Error occurred: ${error.message}`);
  }
  const { instance, accounts } = useMsal();
  const [account, setAccount] = useState<AccountInfo | null>(null);
  const [accessToken] = useState<string | null>(null);
  const [authorizedToken, setAuthorizedToken] = useState<string | null>(null);
  // const [userData, setUserData] = useState<AccountResponse | null>(null);

  useEffect(() => {
    async function requestProfileData() {
      try {
        const response = await instance.acquireTokenSilent({
          ...loginRequest,
          account: accounts[0],
        });
        setAccount(response.account);
        // setAccessToken(response.idToken);
        setAuthorizedToken(response.idToken);

        // get cookie
        await login(
          { name: `${response.account.name}` },
          `${response.idToken}`,
        );

        // Fetch extra data from API
        // const userData = await fetchUser(response.accessToken);
        // setUserData(userData);
        // ------------------------------------------------
        // Calculate the refresh time based on the expiry time
        const tokenExpiryTime = moment(response.expiresOn);
        const refreshTime = Math.max(
          tokenExpiryTime.diff(moment()) - 300000,
          0,
        ); // Refresh 5 minutes before expiry

        setTimeout(async () => {
          try {
            const response = await instance.acquireTokenSilent({
              ...loginRequest,
              account: accounts[0],
              forceRefresh: true,
            });
            setAccount(response.account);
            // setAccessToken(response.idToken);
            setAuthorizedToken(response.idToken);

            // get cookie
            await login(
              { name: `${response.account.name}` },
              `${response.idToken}`,
            );
            // Fetch extra data from API
            // const userData = await fetchUser(response.accessToken);
            // setUserData(userData);
          } catch (error) {
            console.error('Error refreshing token:', error);
          }
        }, refreshTime);
        // -----------------------------------------------------------
      } catch (err) {
        if (err instanceof InteractionRequiredAuthError) {
          try {
            const response = await instance.acquireTokenSilent({
              ...loginRequest,
              account: accounts[0],
              forceRefresh: true,
            });
            setAccount(response.account);
            // setAccessToken(response.idToken);
            setAuthorizedToken(response.idToken);

            // get cookie
            await login(
              { name: `${response.account.name}` },
              `${response.idToken}`,
            );
            // Fetch extra data from API
            // const userData = await fetchUser(response.accessToken);
            // setUserData(userData);
          } catch (error) {
            instance.loginRedirect({
              ...loginRequest,
            });
          }
        } else {
          instance.loginRedirect({
            ...loginRequest,
          });
        }
      }
    }
    if (accounts.length) {
      requestProfileData();
    }
  }, [accounts, accessToken, instance]);
  /*--------------------------------------------------------------------------------------------------------*/

  const context = {
    // accessToken: accessToken,
    authorizedToken: authorizedToken,
  };

  const siteTitle = 'File Copy Utility';
  const queryClient = new QueryClient();

  return (
    <QueryClientProvider client={queryClient}>
      <AppToken.Provider value={JSON.stringify(context)}>
        <Helmet>
          <meta charSet="utf-8" />
          <title>{siteTitle}</title>
        </Helmet>
        <Global styles={globalStyles} />
        <Router basename={process.env.PUBLIC_URL}>
          <Header account={account} />
          <SessionProvider account={account}>
            <div style={{ padding: '4.25rem 0rem', width: '100%' }}>
              <Routes>
                <Route path="/" element={authorizedToken && <HomePage />} />
              </Routes>
              <div
                css={css`
                  .toast-success {
                    --toastify-color-success: ${COLORS.green};
                    --toastify-text-color-light: ${COLORS.white};
                    background-color: ${COLORS.green4};
                  }

                  .toast-error {
                    --toastify-color-error: ${COLORS.red3};
                    --toastify-text-color-light: ${COLORS.white};
                    background-color: ${COLORS.red};
                  }
                `}
              >
                <ToastContainer hideProgressBar style={{ width: '600px' }} />
              </div>
            </div>
          </SessionProvider>
        </Router>
      </AppToken.Provider>
    </QueryClientProvider>
  );
}

export default App;
